// src/pages/owner/OwnerHome.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Stack,
  Button, CircularProgress, Alert, Divider, useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import http from '../../services/http';
import OwnerTopNav from '../../components/owner/OwnerTopNav';
import OwnerBrandFooter from '../../components/owner/OwnerBrandFooter';

function StatCard({ label, value }) {
  return (
    <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.5 }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function buildCmfmLoginUrl(slug) {
  const cmfmOrigin =
    (process.env.REACT_APP_CMFM_ORIGIN || 'https://cmfm.mededtek.in').replace(/\/+$/, '');

  return `${cmfmOrigin}/t/${slug}/cmfm/login`;
}

export default function OwnerHome() {
  const theme = useTheme();
  const [metrics, setMetrics] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      // NOTE: use owner-scoped client so ownerToken is sent
      const [{ data: m }, { data: t }] = await Promise.all([
        http.owner.get('/api/owner/metrics'),
        http.owner.get('/api/owner/tenants'),
      ]);

      setMetrics(m?.metrics || null);
      setTenants(t?.tenants || []);
    } catch (e) {
      const msg =
        e?.data?.message ||
        e?.data?.error ||
        e?.message ||
        'Failed to load metrics';
      setErr(msg);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (err) {
    return (
      <Box p={3}>
        <OwnerTopNav active="home" />
        <Alert severity="error" sx={{ mt: 2 }}>
          {err}
        </Alert>
      </Box>
    );
  }

  if (!metrics || !tenants) {
    return (
      <Box p={3}>
        <OwnerTopNav active="home" />
        <CircularProgress size={24} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box
      p={0}
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <OwnerTopNav active="home" />
      <Box p={3}>
        <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: 1.2 }}>
              MedEdTek Institutional Administration
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
              Multi-institution education and clinical workflow operations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 980, lineHeight: 1.7 }}>
              Manage institutional deployments, academic and training configurations, users, service activity,
              and operational reporting across connected medical education environments.
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <StatCard label="Active Institutional Portals" value={metrics.portals ?? 0} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard label="Total Users" value={metrics.users ?? 0} />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Recorded Visits"
              value={metrics.visits ?? 0}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <StatCard
              label="Interns Trained (Lifetime)"
              value={metrics.internsTrained ?? 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Interns Currently Posted"
              value={metrics.internsCurrentlyPosted ?? 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <StatCard
              label="Interns Left in Batch"
              value={metrics.internsLeftInBatch ?? 0}
            />
          </Grid>
        </Grid>

        <Card sx={{ mt: 3, bgcolor: 'background.paper' }} variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="h6">Institutional Deployments</Typography>
              <Button
                component={RouterLink}
                to="/owner/tenants"
                variant="outlined"
              >
                Manage Deployments
              </Button>
            </Stack>
            <Divider sx={{ mb: 2, borderColor: theme.palette.divider }} />
            <Grid container spacing={2}>
              {tenants.map((t) => (
                <Grid key={t._id || t.slug} item xs={12} md={6}>
                  <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                    <CardContent>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600 }}
                          >
                            {t.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.slug} • {t.dept || 'Dept'}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Users: <b>{t.users}</b> &nbsp;|&nbsp; Visits:{' '}
                            <b>{t.visits}</b>
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            component={RouterLink}
                            to={`/owner/tenants/${t.slug}`}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            href={buildCmfmLoginUrl(t.slug)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
        <OwnerBrandFooter />
      </Box>
    </Box>
  );
}
